howspace.popover = (() => {
    'use strict';

    let FloatingUIDOM;
    let floatingUiLoad = null;
    const loadFloatingUI = () => {
        if (!floatingUiLoad) {
            floatingUiLoad = lib.module('@floating-ui/dom', 'floating-ui.dom.js')
                .then((m) => { FloatingUIDOM = m; })
                .catch((error) => {
                    // Clear the memo so the next open() retries instead of failing for the rest of the page session.
                    floatingUiLoad = null;
                    throw error;
                });
        }
        return floatingUiLoad;
    };

    // Module scoped so that ids are never reused. close() wipes the element's whole data bucket, so a counter
    // stored per element would restart from 1 and a stale open() could match the id of a newer one.
    let openSeq = 0;

    // Both names mean there is nothing to show or hide, e.g. the element was removed from the DOM or replaced
    // mid toggle. Firefox reports InvalidStateError where Chrome reports NotSupportedError. Anything else is a
    // real bug, and reportError surfaces it to the global error logger without breaking the caller.
    const surfaceUnexpectedToggleError = (e) => {
        if (e.name !== 'InvalidStateError' && e.name !== 'NotSupportedError') {
            reportError(e);
        }
    };

    const dataStorage = {};
    const getID = (elem) => {
        if (elem instanceof jQuery) {
            elem = elem[0];
        }
        if (!elem.id) {
            elem.id = 'popover-' + real.uid();
        }
        return elem.id;
    };
    const getData = (elem, key) => {
        const id = getID(elem);
        if (!dataStorage[id]) {
            dataStorage[id] = {};
        }
        return dataStorage[id][key] ?? null;
    };
    const setData = (elem, key, data) => {
        const id = getID(elem);
        if (!dataStorage[id]) {
            dataStorage[id] = {};
        }
        dataStorage[id][key] = data;
    };
    const removeData = (elem, key) => {
        const id = getID(elem);
        if (dataStorage[id]) {
            delete dataStorage[id][key];
        }
    };

    const clearAutoUpdate = (elem) => {
        if (elem instanceof jQuery) {
            elem = elem[0];
        }
        const autoUpdate = getData(elem, 'auto-update');
        if (typeof autoUpdate === 'function') {
            autoUpdate();
        }
        removeData(elem, 'auto-update');
    };

    const isOutsideNormalDocumentFlow = (elem) => {
        if (elem.parentElement) {
            const position = getComputedStyle(elem.parentElement).position;
            if (position === 'absolute' || position === 'fixed' || position === 'sticky') {
                return true;
            }
            return isOutsideNormalDocumentFlow(elem.parentElement);
        }
        return false;
    };

    const getMiddlewares = (flipOptions, margin, arrow, allowScroll, popoverScrollElem, popover) => {
        const middlewares = [
            FloatingUIDOM.offset(margin),
            FloatingUIDOM.shift({padding: margin})
        ];
        if (flipOptions.mainAxis !== false || flipOptions.crossAxis !== false) {
            middlewares.unshift(FloatingUIDOM.flip(flipOptions));
        }

        if (arrow !== null) {
            middlewares.push(FloatingUIDOM.arrow({element: arrow, padding: 16}));
        }

        if (allowScroll) {
            if (popoverScrollElem === null) {
                popoverScrollElem = popover;
            }
            popoverScrollElem = howspace.getDOMElement(popoverScrollElem);
            middlewares.push(FloatingUIDOM.size({
                boundary: document.body,
                apply({availableWidth, availableHeight}) {
                    // Reserve space for sibling content (e.g. a menu-header) outside popoverScrollElem.
                    const siblingContentHeight = popover.offsetHeight - popoverScrollElem.offsetHeight;

                    // Sometimes the size middleware reports the available height as 0 or below.
                    // In that case we don't want to set the max height to a negative value.
                    const availableHeightWithMargin = availableHeight - 16 - siblingContentHeight;
                    if (availableHeightWithMargin > 0) {
                        Object.assign(popoverScrollElem.style, {
                            maxWidth: `${availableWidth}px`,
                            maxHeight: `${availableHeightWithMargin}px`,
                        });
                    }
                }
            }));
        }

        return middlewares;
    };

    const makeSpaceBelow = (elem, distance, padding, reserveSpace) => {
        let moved = false;
        for (const ancestor of new Set(FloatingUIDOM.getOverflowAncestors(elem))) {
            const isWindow = ancestor === window;
            if (!isWindow && !(ancestor instanceof HTMLElement)) {
                continue;
            }
            const scroller = isWindow ? document.scrollingElement : ancestor;
            if ((isWindow && elem.closest('dialog[open]'))
                || (!isWindow && (scroller.matches('dialog')
                    || !/(auto|scroll)/.test(getComputedStyle(scroller).overflowY)))) {
                continue;
            }
            const top = isWindow ? window.visualViewport?.offsetTop ?? 0 : scroller.getBoundingClientRect().top;
            const amount = Math.min(distance, elem.getBoundingClientRect().top - top - padding);
            if (amount <= 0) {
                continue;
            }
            if (scroller.scrollHeight - scroller.clientHeight - scroller.scrollTop < amount) {
                reserveSpace(scroller);
            }
            const before = scroller.scrollTop;
            scroller.scrollBy({top: amount, behavior: 'instant'});
            const delta = scroller.scrollTop - before;
            moved ||= delta > 0;
            distance -= delta;
            if (distance <= 1) {
                break;
            }
        }
        return moved;
    };

    const getViewportMiddlewares = (margin, popover, fittedHeight, reserveSpace, isCurrent) => {
        const padding = margin * 2;
        let makeSpace = fittedHeight === undefined;
        return [
            FloatingUIDOM.offset(margin),
            {
                name: 'spaceBelow',
                async fn(state) {
                    if (!makeSpace) {
                        return {};
                    }
                    makeSpace = false;
                    const overflow = await FloatingUIDOM.detectOverflow(state, {
                        boundary: [], padding, elementContext: 'reference'
                    });
                    if (!isCurrent()) {
                        return {};
                    }
                    // Leave room for filters, actions and a few results before shrinking the list further.
                    const minimumHeight = 20 * parseFloat(getComputedStyle(popover).fontSize);
                    const missingHeight = minimumHeight + margin + overflow.bottom;
                    if (missingHeight > 0
                        && makeSpaceBelow(state.elements.reference, missingHeight, padding, reserveSpace)) {
                        return {reset: {rects: true}};
                    }
                    return {};
                }
            },
            // Size below the button before adjusting the vertical position.
            FloatingUIDOM.shift({boundary: [], padding}),
            FloatingUIDOM.size({
                boundary: [],
                padding,
                apply({availableWidth, availableHeight}) {
                    if (!isCurrent()) {
                        return;
                    }
                    popover.style.setProperty('--popover-max-width', `${Math.max(0, availableWidth)}px`);
                    popover.style.setProperty('--popover-max-height', `${fittedHeight ?? Math.max(0, availableHeight)}px`);
                }
            }),
            {
                ...FloatingUIDOM.shift({boundary: [], padding, mainAxis: false, crossAxis: true}),
                // Separate from the first shift so size does not expand to the whole viewport on its next pass.
                name: 'keepVisible'
            }
        ];
    };

    const open = async (elem, popover, popoverScrollElem = null, {fitViewport = false} = {}) => {
        elem = howspace.getDOMElement(elem);
        popover = howspace.getDOMElement(popover);

        // Loading the positioning library yields to the event loop on the first open of the page. Number each
        // open() call and record the newest number on the element. If the recorded number has changed after the
        // wait, the popover was closed or reopened meanwhile (close() deletes the element's data storage), and
        // this outdated call must not apply its stale state.
        const popoverId = ++openSeq;
        setData(elem, 'popover-id', popoverId);

        await loadFloatingUI();

        if (getData(elem, 'popover-id') !== popoverId) {
            return;
        }

        if (elem.classList.contains('popover-open')) {
            // This popover is already open. Just update its position.
            const updatePosition = getData(elem, 'update-position');
            if (typeof updatePosition === 'function') {
                await updatePosition();
            }
            return;
        }

        elem.classList.add('popover-open');
        if (howspace.isFeatureSupported('popover')) {
            popover.setAttribute('popover', 'manual');
        }
        clearAutoUpdate(elem);

        const arrow = popover.querySelector(':scope > .arrow');
        const arrowShadow = popover.querySelector(':scope > .arrow-shadow');
        const margin = 8; // leave minimum of .5rem units margin
        const flipOptions = {};
        const placement = elem.dataset.placement ?? 'bottom';
        const alignment = elem.dataset.alignment ?? 'center';
        const hasNonStaticParent = isOutsideNormalDocumentFlow(elem);
        const isInsideTopMenu = elem.closest('#top') !== null;
        const allowVerticalFlip = elem.dataset.flipVertical === undefined
            ? hasNonStaticParent && !isInsideTopMenu
            : elem.dataset.flipVertical === 'true';
        const allowHorizontalFlip = elem.dataset.flipHorizontal !== 'false';
        const allowScroll = elem.dataset.scroll === 'true' || hasNonStaticParent;

        if (!allowVerticalFlip) {
            if (placement === 'top' || placement === 'bottom') {
                flipOptions.mainAxis = false;
            } else {
                flipOptions.crossAxis = false;
            }
        }

        if (!allowHorizontalFlip) {
            if (placement === 'left' || placement === 'right') {
                flipOptions.mainAxis = false;
            } else {
                flipOptions.crossAxis = false;
            }
        }

        const options = {
            placement: placement + '-' + alignment,
            strategy: fitViewport || getComputedStyle(popover).position === 'fixed' ? 'fixed' : 'absolute',
            middleware: fitViewport ? [] : getMiddlewares(flipOptions, margin, arrow, allowScroll, popoverScrollElem, popover),
        };

        const scrollPadding = new Map();
        const reserveSpace = (scroller) => {
            if (!scrollPadding.has(scroller)) {
                scrollPadding.set(scroller, {
                    value: scroller.style.getPropertyValue('padding-bottom'),
                    priority: scroller.style.getPropertyPriority('padding-bottom')
                });
                const padding = parseFloat(getComputedStyle(scroller).paddingBottom);
                const space = 20 * parseFloat(getComputedStyle(popover).fontSize);
                scroller.style.setProperty('padding-bottom', `${padding + space}px`);
            }
        };
        let viewportSize;
        let fittedHeight;
        let positionSeq = 0;
        const updatePosition = () => {
            const positionId = ++positionSeq;
            const isCurrent = () => getData(elem, 'update-position') === updatePosition && positionId === positionSeq;
            if (fitViewport) {
                const viewport = window.visualViewport;
                const currentSize = `${viewport?.width ?? window.innerWidth}:${viewport?.height ?? window.innerHeight}`;
                if (currentSize !== viewportSize) {
                    fittedHeight = undefined;
                    viewportSize = currentSize;
                }
                // Make space on opening and keyboard/viewport resizing, not while the user scrolls the page.
                options.middleware = getViewportMiddlewares(margin, popover, fittedHeight, reserveSpace, isCurrent);
            } else {
                Object.assign(popover.style, {
                    maxWidth: '',
                    maxHeight: '',
                });
            }
            return FloatingUIDOM.computePosition(elem, popover, options).then(data => {
                if (!isCurrent()) {
                    return;
                }
                if (fitViewport && fittedHeight === undefined) {
                    fittedHeight = popover.getBoundingClientRect().height;
                }
                Object.assign(popover.style, {
                    left: `${data.x}px`,
                    top: `${data.y}px`,
                });

                if (arrow !== null) {
                    const arrowX = data.middlewareData.arrow.x || null;
                    const arrowY = data.middlewareData.arrow.y || null;
                    const staticSide = {
                        top: 'bottom',
                        right: 'left',
                        bottom: 'top',
                        left: 'right',
                    }[data.placement.split('-')[0]];

                    const styles = {
                        left: arrowX !== null ? `${arrowX}px` : '',
                        top: arrowY !== null ? `${arrowY}px` : '',
                        right: '',
                        bottom: '',
                        [staticSide]: '-5px',
                    };
                    Object.assign(arrow.style, styles);
                    Object.assign(arrowShadow.style, styles);
                }
            });
        };

        setData(elem, 'update-position', updatePosition);

        // TODO: here we could register the popover to a ResizeObserver to execute updatePosition whenever the popover
        // size changes (if performance becomes a problem then with a brief timeout to limit the number of updates).

        if (howspace.isFeatureSupported('popover')) {
            try {
                popover.showPopover();
            } catch (e) {
                surfaceUnexpectedToggleError(e);
            }
        }

        let stopAutoUpdate;
        setData(elem, 'auto-update', () => {
            stopAutoUpdate?.();
            for (const [scroller, original] of scrollPadding) {
                scroller.style.setProperty('padding-bottom', original.value, original.priority);
            }
        });
        await updatePosition();
        if (getData(elem, 'popover-id') !== popoverId) {
            return;
        }
        stopAutoUpdate = FloatingUIDOM.autoUpdate(elem, popover, () => {
            updatePosition().catch(reportError);
        });

        howspace.dispatchEvent(elem, howspace.popover.POPOVER_OPENED);
    }

    const close = (elem, popover) => {
        elem = howspace.getDOMElement(elem);
        popover = howspace.getDOMElement(popover);

        // Record which open() this close targets. The body below runs deferred, so a newer open() may stamp the
        // element while this close is still queued. That makes this close stale, and it must not tear down the
        // newer popover's state.
        const closeId = getData(elem, 'popover-id');

        real.onTierReady(real.TIER_2, () => {
            if (getData(elem, 'popover-id') !== closeId) {
                return;
            }

            clearAutoUpdate(elem);
            delete dataStorage[getID(elem)];

            if (elem.classList.contains('popover-open')) {
                elem.classList.remove('popover-open');
                // popover is null when the caller's node lookup came up empty, then there is nothing to hide.
                if (popover && howspace.isFeatureSupported('popover')) {
                    try {
                        popover.hidePopover();
                    } catch (e) {
                        surfaceUnexpectedToggleError(e);
                    }
                }
                howspace.dispatchEvent(elem, howspace.popover.POPOVER_CLOSED);
            }
        });
    };

    // Opt-in interaction handling for content opened by a button. Existing callers still own their lifecycle.
    const bind = (trigger, content, {
        onOpen, onClose, getInitialFocus, getReturnFocus = () => trigger
    } = {}) => {
        trigger = howspace.getDOMElement(trigger);
        content = howspace.getDOMElement(content);
        const controller = new AbortController();
        const options = {signal: controller.signal};
        const originalVisibility = content.style.visibility;
        let opened = false;
        let dismissController;

        trigger.setAttribute('aria-controls', content.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.dataset.alignment ??= 'start';
        trigger.dataset.scroll ??= 'true';
        trigger.dataset.placement = 'bottom';
        trigger.dataset.flipVertical = 'false';
        trigger.dataset.flipHorizontal = 'false';

        const hide = (restoreFocus = true) => {
            if (!opened) {
                return;
            }
            opened = false;
            dismissController?.abort();
            trigger.setAttribute('aria-expanded', 'false');
            close(trigger, content);
            content.hidden = true;
            content.style.visibility = originalVisibility;
            onClose?.();
            const target = getReturnFocus();
            if (restoreFocus && target?.isConnected && !target.disabled) {
                target.focus({preventScroll: true});
                // Apply can change the selected list after closing. Reveal the final focus position if needed.
                requestAnimationFrame(() => {
                    if (!opened && target.isConnected && document.activeElement === target) {
                        const viewport = window.visualViewport;
                        let top = viewport?.offsetTop ?? 0;
                        let bottom = top + (viewport?.height ?? window.innerHeight);
                        for (const ancestor of FloatingUIDOM?.getOverflowAncestors(target) ?? []) {
                            if (ancestor instanceof HTMLElement) {
                                const bounds = ancestor.getBoundingClientRect();
                                top = Math.max(top, bounds.top);
                                bottom = Math.min(bottom, bounds.bottom);
                            }
                        }
                        const bounds = target.getBoundingClientRect();
                        if (bounds.top < top || bounds.bottom > bottom) {
                            target.scrollIntoView({block: 'center', inline: 'nearest', behavior: 'instant'});
                        }
                    }
                });
            }
        };

        const show = async () => {
            opened = true;
            trigger.setAttribute('aria-expanded', 'true');
            // Keep the content measurable without displaying it before its position is ready.
            content.style.visibility = 'hidden';
            content.style.removeProperty('--popover-max-width');
            content.style.removeProperty('--popover-max-height');
            content.hidden = false;
            dismissController = new AbortController();
            const dismissOptions = {signal: dismissController.signal};
            document.addEventListener('click', event => {
                if (!content.contains(event.target) && !trigger.contains(event.target)) {
                    // Preserve focus on outside controls, but do not leave it in the hidden panel.
                    hide(content.contains(document.activeElement));
                }
            }, dismissOptions);
            document.addEventListener('keydown', event => {
                if (event.key === 'Escape' && !content.querySelector('.dropdown.expanded')) {
                    event.preventDefault();
                    event.stopPropagation();
                    hide();
                }
            }, {...dismissOptions, capture: true});

            try {
                await open(trigger, content, null, {fitViewport: true});
                if (dismissOptions.signal.aborted || controller.signal.aborted) {
                    return;
                }
                if (!content.isConnected) {
                    hide(false);
                    return;
                }
                content.style.visibility = originalVisibility;
                onOpen?.();
                const target = getInitialFocus?.() ?? $(content).find('input:not([type="hidden"]), button, select, textarea')
                    .filter(':visible:not(:disabled)').not('[data-close-popover]').first()[0];
                (target ?? content).focus({preventScroll: true});
            } catch (error) {
                if (!dismissOptions.signal.aborted) {
                    hide();
                }
                reportError(error);
            }
        };

        trigger.addEventListener('click', () => opened ? hide() : show(), options);
        content.addEventListener('click', event => {
            if (event.target.closest('[data-close-popover]')) {
                hide();
            }
        }, options);

        return {
            close: hide,
            destroy: () => {
                hide(false);
                controller.abort();
            }
        };
    };

    return {
        POPOVER_OPENED: 'popover-opened',
        POPOVER_CLOSED: 'popover-closed',
        open: open,
        close: close,
        bind: bind
    };
})();
